import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { site, nav } from "../data/mock";
import { useDeposit } from "../context/DepositContext";
import api from "../lib/api";
import LanguageSwitcher from "./LanguageSwitcher";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  Menu, Phone, Mail, MapPin, ChevronDown, Facebook, Instagram,
  MessageCircle, Anchor, Compass, Lock,
} from "lucide-react";

const LK = {
  "Home": "nav.home", "Trips": "nav.trips", "Fishing": "nav.fishing", "Gallery": "nav.gallery",
  "Blog": "nav.blog", "About": "nav.about", "FAQ": "nav.faq", "Contact": "nav.contact",
  "Half Day Tour": "nav.halfDayTour", "Full Day Inshore": "nav.fullDayInshore", "Full Day Offshore": "nav.fullDayOffshore",
  "Surf Drops": "nav.surfDrops", "Sunset Cruise": "nav.sunsetCruise", "Whale Watching": "nav.whaleWatching",
  "San Juan del Sur Fishing": "nav.sjdsFishing", "Offshore Species": "nav.offshoreSpecies",
  "Inshore Species": "nav.inshoreSpecies", "Fishing Calendar": "nav.fishingCalendar", "Marine Weather": "nav.weather",
  "All Species": "nav.allSpecies",
};

const WhatsAppFloat = () => (
  <a
    href={`https://wa.me/${site.whatsapp}?text=Hi%20Hog%20Tide!%20I'd%20like%20to%20book%20a%20fishing%20trip.`}
    target="_blank"
    rel="noreferrer"
    className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-white shadow-2xl transition hover:scale-105 hover:bg-[#20bd5a]"
    aria-label="Chat on WhatsApp"
  >
    <MessageCircle className="h-6 w-6" />
    <span className="hidden sm:inline font-semibold text-sm pr-1">Chat with us</span>
  </a>
);

const Header = () => {
  const { t } = useTranslation();
  const nl = (label) => (LK[label] ? t(LK[label]) : label);
  const { openDeposit } = useDeposit();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-40">
      {/* Top bar */}
      <div className="hidden md:block bg-[#071726] text-slate-300 text-xs">
        <div className="container-x flex items-center justify-between h-9">
          <div className="flex items-center gap-5">
            <a href={`tel:${site.phoneRaw}`} className="flex items-center gap-1.5 hover:text-[#f5a524]"><Phone className="h-3.5 w-3.5" /> {site.phone}</a>
            <a href={`mailto:${site.email}`} className="flex items-center gap-1.5 hover:text-[#f5a524]"><Mail className="h-3.5 w-3.5" /> {site.email}</a>
            <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {site.location}</span>
          </div>
          <div className="flex items-center gap-3">
            <a href={site.social.facebook} target="_blank" rel="noreferrer" className="hover:text-[#f5a524]"><Facebook className="h-4 w-4" /></a>
            <a href={site.social.instagram} target="_blank" rel="noreferrer" className="hover:text-[#f5a524]"><Instagram className="h-4 w-4" /></a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`transition-all duration-300 ${scrolled ? "bg-[#0a1f33]/95 backdrop-blur shadow-lg" : "bg-[#0a1f33]/80 backdrop-blur"}`}>
        <div className="container-x flex items-center justify-between h-[74px]">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <span className="inline-flex items-center bg-white rounded-xl px-2.5 py-1.5 shadow-sm">
                <img src={site.logo} alt="Hog Tide Elite Fishing Tours" className="h-9 w-auto" />
              </span>
            </Link>
            <LanguageSwitcher />
          </div>

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((item) => (
              <div key={item.to} className="relative group">
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-1 px-3 py-2 text-sm font-medium tracking-wide transition ${
                      isActive ? "text-[#f5a524]" : "text-slate-200 hover:text-[#f5a524]"
                    }`
                  }
                >
                  {nl(item.label)}
                  {item.children && <ChevronDown className="h-3.5 w-3.5" />}
                </NavLink>
                {item.children && (
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="w-60 rounded-xl border border-white/10 bg-[#0e2740] p-2 shadow-2xl">
                      {item.children.map((c) => (
                        <Link key={c.to} to={c.to} className="block rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/10 hover:text-[#f5a524]">
                          {nl(c.label)}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <Link to="/plan-your-trip">
              <Button variant="ghost" className="text-slate-100 hover:text-[#f5a524] hover:bg-white/5 gap-1.5">
                <Compass className="h-4 w-4" /> {t("common.planYourTrip")}
              </Button>
            </Link>
            <Button className="btn-gold gap-1.5" onClick={() => openDeposit()}>
              <MessageCircle className="h-4 w-4" /> {t("common.bookWhatsApp")}
            </Button>
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex items-center gap-2">
            <a href={`tel:${site.phoneRaw}`}><Button size="icon" variant="ghost" className="text-white"><Phone className="h-5 w-5" /></Button></a>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button size="icon" variant="ghost" className="text-white"><Menu className="h-6 w-6" /></Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0a1f33] border-white/10 text-white w-[300px] overflow-y-auto">
                <span className="inline-flex bg-white rounded-xl px-2.5 py-1.5 mb-6 w-fit"><img src={site.logo} alt="Hog Tide Elite Fishing Tours" className="h-9 w-auto" /></span>
                <div className="flex flex-col gap-1">
                  {nav.map((item) => (
                    <div key={item.to}>
                      <Link to={item.to} onClick={() => setOpen(false)} className="block py-2 font-heading text-lg hover:text-[#f5a524]">{nl(item.label)}</Link>
                      {item.children && (
                        <div className="ml-3 mb-1 flex flex-col border-l border-white/10 pl-3">
                          {item.children.map((c) => (
                            <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="py-1.5 text-sm text-slate-300 hover:text-[#f5a524]">{nl(c.label)}</Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex flex-col gap-2">
                  <Link to="/plan-your-trip" onClick={() => setOpen(false)}><Button variant="outline" className="w-full border-[#f5a524] text-[#f5a524] bg-transparent">{t("common.planYourTrip")}</Button></Link>
                  <Button className="btn-gold w-full" onClick={() => { setOpen(false); openDeposit(); }}>{t("common.bookWhatsApp")}</Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  const { openDeposit } = useDeposit();
  return (
    <footer className="bg-[#071726] text-slate-300 pt-14 pb-8 mt-10 border-t border-white/5">
      <div className="container-x grid gap-10 md:grid-cols-4">
        <div>
          <span className="inline-flex bg-white rounded-xl px-3 py-2 mb-4 w-fit"><img src={site.logo} alt="Hog Tide Elite Fishing Tours" className="h-10 w-auto" /></span>
          <p className="text-sm text-slate-400 leading-relaxed">
            {t("footer.tagline")}
          </p>
          <div className="flex gap-3 mt-4">
            <a href={site.social.facebook} target="_blank" rel="noreferrer" className="rounded-full bg-white/5 p-2 hover:bg-[#f5a524] hover:text-[#0a1f33]"><Facebook className="h-4 w-4" /></a>
            <a href={site.social.instagram} target="_blank" rel="noreferrer" className="rounded-full bg-white/5 p-2 hover:bg-[#f5a524] hover:text-[#0a1f33]"><Instagram className="h-4 w-4" /></a>
            <a href={`https://wa.me/${site.whatsapp}`} target="_blank" rel="noreferrer" className="rounded-full bg-white/5 p-2 hover:bg-[#25D366] hover:text-white"><MessageCircle className="h-4 w-4" /></a>
          </div>
        </div>
        <div>
          <h4 className="font-heading text-white text-lg mb-3">{t("nav.trips")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/trips/half-day-tour" className="hover:text-[#f5a524]">{t("nav.halfDayTour")}</Link></li>
            <li><Link to="/trips/full-day-inshore" className="hover:text-[#f5a524]">{t("nav.fullDayInshore")}</Link></li>
            <li><Link to="/trips/full-day-offshore" className="hover:text-[#f5a524]">{t("nav.fullDayOffshore")}</Link></li>
            <li><Link to="/trips/surf-drops" className="hover:text-[#f5a524]">{t("nav.surfDrops")}</Link></li>
            <li><Link to="/trips/sunset-cruise" className="hover:text-[#f5a524]">{t("nav.sunsetCruise")}</Link></li>
            <li><Link to="/trips/whale-watching" className="hover:text-[#f5a524]">{t("nav.whaleWatching")}</Link></li>
            <li><Link to="/plan-your-trip" className="hover:text-[#f5a524]">{t("common.planYourTrip")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading text-white text-lg mb-3">{t("nav.fishing")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/san-juan-del-sur-fishing" className="hover:text-[#f5a524]">{t("nav.sjdsFishing")}</Link></li>
            <li><Link to="/offshore-species" className="hover:text-[#f5a524]">{t("nav.offshoreSpecies")}</Link></li>
            <li><Link to="/inshore-species" className="hover:text-[#f5a524]">{t("nav.inshoreSpecies")}</Link></li>
            <li><Link to="/bottom-species" className="hover:text-[#f5a524]">Bottom Species</Link></li>
            <li><Link to="/book" className="hover:text-[#f5a524]">Book &amp; Reviews</Link></li>
            <li><a href="https://fishingbooker.com/charters/view/11081?utm_source=share_charter&utm_medium=copy_link" target="_blank" rel="noreferrer" className="hover:text-[#f5a524]">Book on FishingBooker</a></li>
            <li><Link to="/fishing-calendar" className="hover:text-[#f5a524]">{t("nav.fishingCalendar")}</Link></li>
            <li><Link to="/weather" className="hover:text-[#f5a524]">{t("nav.weather")}</Link></li>
            <li><Link to="/gallery" className="hover:text-[#f5a524]">{t("nav.gallery")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading text-white text-lg mb-3">{t("footer.getInTouch")}</h4>
          <ul className="space-y-2 text-sm">
            <li><a href={`tel:${site.phoneRaw}`} className="flex items-center gap-2 hover:text-[#f5a524]"><Phone className="h-4 w-4" /> {site.phone}</a></li>
            <li><a href={`mailto:${site.email}`} className="flex items-center gap-2 hover:text-[#f5a524]"><Mail className="h-4 w-4" /> {site.email}</a></li>
            <li className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {site.location}</li>
          </ul>
          <Button className="btn-gold w-full mt-4" onClick={() => openDeposit()}>{t("common.bookWhatsApp")}</Button>
        </div>
      </div>
      <div className="container-x mt-10 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <p>© {new Date().getFullYear()} Hog Tide Fishing. {t("footer.rights")}</p>
        <div className="flex items-center gap-4">
          <span>Sport Fishing Charters · San Juan del Sur, Nicaragua</span>
          <Link to="/admin" className="inline-flex items-center gap-1 hover:text-[#f5a524]"><Lock className="h-3 w-3" /> {t("footer.ownerLogin")}</Link>
        </div>
      </div>
      <div className="container-x mt-4 text-center text-[11px] text-slate-600">
        Website Created by <a href="https://www.GregMiceli.com" target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#f5a524] transition" data-testid="footer-credit">www.GregMiceli.com</a>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    api.track(pathname);
  }, [pathname]);
  return (
    <div className="App">
      <Header />
      <main>{children}</main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Layout;
